import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react'
import { Library, MoreHorizontal, Redo2, Undo2 } from 'lucide-react'
import { useDismiss } from '../../lib/hooks'
import { useTheme } from '../../lib/theme'
import { btn, SwitchTrack } from '../../components/ui'

export interface ComposerToolBarProps {
  /** "Draft · saved just now" */
  status: string
  statusTitle?: string
  published: boolean
  focusMode: boolean
  historyOpen: boolean
  onHistory: () => void
  onFocus: () => void
  onPreview: () => void
  onPublish: () => void
  /** The word count and read time in the page's corner. */
  showCount: boolean
  onShowCount: (on: boolean) => void
  /**
   * The audio dock is open ("Recording", "Listening", "Syncing", "Voice
   * memo"): the bar says so and offers a quiet way out instead of its
   * actions, so nothing navigates away mid-take.
   */
  dock?: { label: string; live?: boolean; onExit: () => void } | null
  /** Undo and redo for block operations (moving, sizing, cropping, inserting, removing). */
  undo?: { canUndo: boolean; canRedo: boolean; onUndo: () => void; onRedo: () => void }
  /**
   * Desks only (phones have the button on the bottom edge): the square
   * Thoughts button at the bar's right end (Niki, 10/6: back in the bar, after
   * trying it floating under it), so the thoughts panel is always one click
   * away as the page scrolls.
   */
  thoughts?: { open: boolean; today?: number; onToggle: () => void } | null
}

/**
 * The writing's own tools, under Burrow's nav and never in it (Niki, 9/30):
 * everything sits together on the right (Niki, 10/1) — save state, History
 * and Focus, then Preview and Publish. It is the one bar that stays pinned as the page scrolls, and it
 * draws its hairline only once it has something scrolling under it. In Focus
 * mode it shrinks to the save state and a way out. As it narrows, the
 * quieter items fold into the "···" menu.
 */
export function ComposerToolBar(props: ComposerToolBarProps) {
  const { status, statusTitle, published, focusMode, historyOpen, onHistory, onFocus, onPreview, onPublish, showCount, onShowCount, dock, undo, thoughts } = props
  const stuck = useStuck()
  const statusText = (
    <span role="status" title={statusTitle} className="truncate text-[13px] font-medium text-muted-foreground">
      {status}
    </span>
  )

  let left = statusText
  let right
  if (dock) {
    right = (
      <>
        <span className="flex items-center gap-1.5 rounded-full bg-accent/15 px-2.5 py-1 text-[13px] font-semibold text-accent">
          <span aria-hidden className={`size-1.5 rounded-full bg-accent ${dock.live ? 'animate-pulse' : ''}`} />
          {dock.label}
        </span>
        <button type="button" onClick={dock.onExit} className={btn.ghost} title="Esc">
          Exit
        </button>
      </>
    )
  } else if (focusMode) {
    right = (
      <button type="button" onClick={onFocus} aria-pressed className={btn.ghost} title="Esc">
        Exit focus
      </button>
    )
  } else {
    left = (
      <>
        <span className="max-md:hidden">{statusText}</span>
        {undo && <UndoButtons {...undo} />}
        {/* max-lg:hidden, not "hidden lg:inline-flex": the button base already sets inline-flex. */}
        <button type="button" onClick={onHistory} aria-expanded={historyOpen} className={`${btn.ghost} max-lg:hidden`}>
          History
        </button>
        <button type="button" onClick={onFocus} aria-pressed={false} className={`${btn.ghost} max-lg:hidden`}>
          Focus
        </button>
      </>
    )
    right = (
      <>
        <button type="button" onClick={onPreview} className={`${btn.outline} max-sm:hidden`}>
          Preview
        </button>
        <button type="button" onClick={onPublish} className={btn.primary}>
          {published ? 'Update' : 'Publish'}
        </button>
        <MoreMenu status={status} showCount={showCount} onShowCount={onShowCount} onHistory={onHistory} onFocus={onFocus} onPreview={onPreview} />
        {thoughts && <ThoughtsButton {...thoughts} />}
      </>
    )
  }

  return (
    <>
      <div ref={stuck.sentinel} aria-hidden className="h-px -mb-px" />
      <section
        aria-label="Writing tools"
        className={`sticky top-0 z-30 border-b transition-[border-color,background-color,opacity] duration-200 ${
          stuck.on ? 'border-border/40 bg-canvas/90 backdrop-blur-md' : 'border-transparent bg-canvas'
        } ${focusMode && !dock ? 'opacity-40 focus-within:opacity-100 hover:opacity-100' : ''}`}
      >
        <div className="flex h-12 items-center justify-end gap-3 px-4 font-sans sm:px-6">
          <div className="flex min-w-0 items-center justify-end gap-1 sm:gap-1.5">{left}</div>
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">{right}</div>
        </div>
      </section>
    </>
  )
}

const MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)

/**
 * Undo and redo, beside the save state they change. They step back block
 * operations — a move, a resize, a crop, an insert or a removal — not typing,
 * which keeps the browser's own undo. Pressing them doesn't take the caret
 * out of the paragraph it's in.
 */
function UndoButtons({ canUndo, canRedo, onUndo, onRedo }: { canUndo: boolean; canRedo: boolean; onUndo: () => void; onRedo: () => void }) {
  const icon =
    'flex size-8 items-center justify-center rounded-xl text-muted-foreground transition-all hover:bg-muted/30 hover:text-foreground disabled:pointer-events-none disabled:opacity-35'
  const keep = (e: MouseEvent) => e.preventDefault()
  return (
    <span className="flex items-center">
      <button type="button" aria-label="Undo" title={MAC ? 'Undo (⌘Z)' : 'Undo (Ctrl+Z)'} disabled={!canUndo} onMouseDown={keep} onClick={onUndo} className={icon}>
        <Undo2 size={17} aria-hidden />
      </button>
      <button type="button" aria-label="Redo" title={MAC ? 'Redo (⇧⌘Z)' : 'Redo (Ctrl+Y)'} disabled={!canRedo} onMouseDown={keep} onClick={onRedo} className={icon}>
        <Redo2 size={17} aria-hidden />
      </button>
    </span>
  )
}

/**
 * The way into your thoughts on a desk: a square, not a round bubble, which
 * reads as a chatbot (Niki, 10/2). It is the main app's References button
 * (FloatingAccentButton: the accent-filled rounded-xl square with a shadow and
 * a 16px icon), with its dot when there are thoughts from today. While the
 * panel is open it shows pressed, and closes it. The icon is Library, the one
 * Burrow's nav gives the place your thoughts live (it was a lightbulb, which
 * read as "ideas" or "tips" rather than "your captured thoughts").
 */
function ThoughtsButton({ open, today = 0, onToggle }: { open: boolean; today?: number; onToggle: () => void }) {
  const title = open ? 'Close thoughts' : today > 0 ? `Thoughts — ${today.toLocaleString()} from today` : 'Thoughts'
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-label={open ? 'Close thoughts' : 'Open thoughts'}
      title={title}
      className={`relative shrink-0 rounded-xl border border-accent bg-accent p-2.5 text-accent-foreground shadow-lg transition-opacity hover:opacity-90 ${
        open ? 'ring-2 ring-accent/35 ring-offset-2 ring-offset-canvas' : ''
      }`}
    >
      <Library size={16} aria-hidden />
      {today > 0 && !open && (
        // The app's showDot: --primary, so it belongs to the palette in both themes.
        <span aria-hidden className="absolute -right-1 -top-1 size-2.5 rounded-full border-2 border-background bg-primary" />
      )}
    </button>
  )
}

/** Whether the bar is pinned with the page scrolling under it: its sentinel has left the screen. */
function useStuck() {
  const sentinel = useRef<HTMLDivElement>(null)
  const [on, setOn] = useState(false)
  useEffect(() => {
    const el = sentinel.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver(([entry]) => setOn(!entry.isIntersecting))
    io.observe(el)
    return () => io.disconnect()
  }, [])
  return { sentinel, on }
}

function MoreMenu({
  status,
  showCount,
  onShowCount,
  onHistory,
  onFocus,
  onPreview,
}: {
  status: string
  showCount: boolean
  onShowCount: (on: boolean) => void
  onHistory: () => void
  onFocus: () => void
  onPreview: () => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const close = useCallback(() => setOpen(false), [])
  useDismiss(ref, open, close)
  const { theme, toggleTheme } = useTheme()
  const item = 'rounded-lg px-3 py-2 text-left text-foreground hover:bg-muted/40'
  const run = (fn: () => void) => () => {
    setOpen(false)
    fn()
  }
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More"
        onClick={() => setOpen((o) => !o)}
        className={`flex size-8 items-center justify-center rounded-xl text-muted-foreground transition-all hover:bg-muted/30 hover:text-foreground ${open ? 'bg-muted/30 text-foreground' : ''}`}
      >
        <MoreHorizontal size={18} aria-hidden />
      </button>
      {open && (
        <div
          role="menu"
          aria-label="More"
          className="absolute right-0 top-10 z-40 flex w-64 flex-col gap-0.5 rounded-2xl border border-border/50 bg-card p-1.5 text-[13px] font-medium shadow-[var(--shadow-pop)]"
        >
          <span className="px-3 py-2 text-muted-foreground md:hidden">{status}</span>
          <button type="button" role="menuitem" className={`${item} lg:hidden`} onClick={run(onHistory)}>
            History
          </button>
          <button type="button" role="menuitem" className={`${item} lg:hidden`} onClick={run(onFocus)}>
            Focus
          </button>
          <button type="button" role="menuitem" className={`${item} sm:hidden`} onClick={run(onPreview)}>
            Preview
          </button>
          <div className="mx-2 my-1 h-px bg-border/60 lg:hidden" />
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={showCount}
            className={`${item} flex items-center justify-between gap-3`}
            onClick={() => onShowCount(!showCount)}
          >
            Word count &amp; read time
            <SwitchTrack on={showCount} />
          </button>
          {/* Light and dark were only in the nav's sun/moon and the reader's pill (Niki, 10/5: "I don't know where it is"). */}
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={theme === 'dark'}
            className={`${item} flex items-center justify-between gap-3`}
            onClick={toggleTheme}
          >
            Dark mode
            <SwitchTrack on={theme === 'dark'} />
          </button>
        </div>
      )}
    </div>
  )
}
